import type { ExprNode, UserNode } from '@revelio/data-access';

import { DEFAULT_ACTIVITY_TAXONOMY_ID } from '../../../filters/contexts/defaultFilters';
import type { CustomActivityTaxonomySelection } from '../../../filters/definitions/activityTaxonomy';
import { findFilterById } from '../../../filters/engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  type SingleValueFilter,
  type TreeFilter,
} from '../../../filters/types/filters';
import { getNumericIdFromNestedId } from '../../../filters/utils/get-numeric-id-from-nested-id';
import { getActivityLevelForLevelId } from '../../../filters/utils/role-skill-ent-kind';
import { buildOrNode, wrapExprNode } from './node-builders';

export const getActivityTaxonomyId = (
  filters: Filter[]
): number | undefined => {
  const activityTaxonomyFilter = findFilterById(
    filters,
    FilterId.ACTIVITY_TAXONOMY
  );
  if (
    !activityTaxonomyFilter ||
    activityTaxonomyFilter.type !== FilterType.VALUE
  ) {
    return undefined;
  }

  const value = (
    activityTaxonomyFilter as SingleValueFilter<CustomActivityTaxonomySelection>
  ).value;
  if (value && typeof value.taxonomyId === 'number') {
    return value.taxonomyId;
  }
  return DEFAULT_ACTIVITY_TAXONOMY_ID;
};

const buildActivityValNode = (
  id: number,
  level: number,
  taxonomyId: number,
  curr: boolean
): ExprNode => ({
  val: { pos_activity: { id, level, taxonomy_id: taxonomyId, curr } },
});

/**
 * Build the activity expression from a tree filter. Unlike role/skill, activity
 * has no legacy `pos_attribute` kind — it is a dedicated `pos_activity` node
 * (like the newer `pos_role`), so it can't route through the tag-based
 * `buildTreeFilterNodes`. Selections are grouped by level and OR-ed within and
 * across levels, matching the tree-filter semantics of the other dimensions.
 */
const buildActivityExpr = (
  filter: TreeFilter,
  taxonomyId: number,
  curr: boolean
): ExprNode | null => {
  const selections = filter.primarySelections ?? filter.selections;
  if (!selections || selections.length === 0) return null;

  const nodesByLevel = new Map<number, ExprNode[]>();
  for (const selection of selections) {
    const level = getActivityLevelForLevelId(selection.levelId);
    if (level === null) continue;
    const id = getNumericIdFromNestedId(String(selection.id));
    if (id === null) continue;
    const node = buildActivityValNode(id, level, taxonomyId, curr);
    const existing = nodesByLevel.get(level);
    if (existing) {
      existing.push(node);
    } else {
      nodesByLevel.set(level, [node]);
    }
  }

  const levelNodes = Array.from(nodesByLevel.values()).map((nodes) =>
    nodes.length === 1 ? (nodes[0] as ExprNode) : buildOrNode(nodes)
  );

  if (levelNodes.length === 0) return null;
  if (levelNodes.length === 1) return levelNodes[0] as ExprNode;
  return buildOrNode(levelNodes);
};

export const buildActivityFilter = (filters: Filter[]): UserNode | null => {
  const activityFilter = findFilterById(filters, FilterId.ACTIVITY);
  if (!activityFilter || activityFilter.type !== FilterType.TREE) {
    return null;
  }

  const exprNode = buildActivityExpr(
    activityFilter,
    getActivityTaxonomyId(filters) ?? DEFAULT_ACTIVITY_TAXONOMY_ID,
    activityFilter.isCurrent ?? true
  );
  if (!exprNode) return null;

  return wrapExprNode(exprNode, activityFilter.isNot);
};

/**
 * Build the MULTI_ACTIVITY node from filter state. Each instance is its own OR
 * group and the instances are AND-ed together — returned as an array so
 * `composeFilterNodes` AND-s them alongside the other dimensions instead of
 * nesting another AND node. Mirrors `buildMultiCustomSkillFilter`.
 */
export const buildMultiActivityFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiActivity = findFilterById(filters, FilterId.MULTI_ACTIVITY);
  if (!multiActivity || !isMultiFilter(multiActivity)) {
    return null;
  }
  if (multiActivity.instances.length === 0) {
    return null;
  }

  const taxonomyId =
    getActivityTaxonomyId(filters) ?? DEFAULT_ACTIVITY_TAXONOMY_ID;

  const instanceNodes = multiActivity.instances
    .filter((inst): inst is TreeFilter => inst?.type === FilterType.TREE)
    .map((instance) => {
      const exprNode = buildActivityExpr(
        instance,
        taxonomyId,
        instance.isCurrent ?? true
      );
      if (!exprNode) return null;
      return wrapExprNode(exprNode, instance.isNot);
    })
    .filter((node): node is UserNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};
